import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { generateBrief, writeContent, regenerateMeta, exportContentDoc } from '../utils/api';

const STAGE_COLORS = {
  'Attention': { bg: '#E6F1FB', color: '#0C447C', border: '#85B7EB' },
  'Interest':  { bg: '#EAF3DE', color: '#27500A', border: '#97C459' },
  'Desire':    { bg: '#FAEEDA', color: '#633806', border: '#FAC775' },
  'E-A-T':     { bg: '#EEEDFE', color: '#26215C', border: '#AFA9EC' },
  'Action':    { bg: '#FAECE7', color: '#4A1B0C', border: '#F0997B' },
  'Additional':{ bg: '#F1EFE8', color: '#2C2C2A', border: '#B4B2A9' },
};

function CharCounter({ text, limit }) {
  const len = (text || '').length;
  const color = len > limit ? 'var(--red)' : len > limit * 0.9 ? 'var(--amber)' : 'var(--green)';
  return <span style={{ fontSize: 11, fontWeight: 600, color }}>{len}/{limit} {len > limit ? '⚠' : '✓'}</span>;
}

// ── Step 1: Keyword & client input ────────────────────────────
function StepInput({ clients, onNext }) {
  const [form, setForm] = useState({
    keyword: '', service: '', location: '', clientId: '',
    businessName: '', description: '', referenceUrl: ''
  });
  const [error, setError] = useState('');
  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleClientChange = (id) => {
    const c = clients.find(c => c.id === id);
    update('clientId', id);
    if (c) { update('businessName', c.name); update('description', c.description || ''); }
  };

  const handleNext = () => {
    if (!form.keyword.trim()) { setError('Please enter a target keyword.'); return; }
    if (!form.businessName.trim()) { setError('Please enter a business name.'); return; }
    onNext(form);
  };

  return (
    <div className="card">
      <div className="card-title">Keyword & client details</div>
      <div className="card-sub">Enter the keyword and client information for this content brief.</div>

      <div className="form-group">
        <label>Link to client (optional)</label>
        <select value={form.clientId} onChange={e => handleClientChange(e.target.value)}>
          <option value="">— No client selected —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="form-row form-row-2 form-group">
        <div>
          <label>Target keyword *</label>
          <input type="text" placeholder="e.g. carpet cleaning bromley" value={form.keyword} onChange={e => update('keyword', e.target.value)} autoFocus />
        </div>
        <div>
          <label>Service</label>
          <input type="text" placeholder="e.g. Carpet cleaning" value={form.service} onChange={e => update('service', e.target.value)} />
        </div>
      </div>

      <div className="form-row form-row-2 form-group">
        <div>
          <label>Location (optional)</label>
          <input type="text" placeholder="e.g. Bromley" value={form.location} onChange={e => update('location', e.target.value)} />
        </div>
        <div>
          <label>Business name *</label>
          <input type="text" placeholder="e.g. Carpet Bright UK" value={form.businessName} onChange={e => update('businessName', e.target.value)} />
        </div>
      </div>

      <div className="form-group">
        <label>Business description</label>
        <textarea rows={2} placeholder="Brief description of what the business does" value={form.description} onChange={e => update('description', e.target.value)} />
      </div>

      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Reference URL (existing page, if any)</label>
        <input type="url" placeholder="https://clientsite.co.uk/existing-page/" value={form.referenceUrl} onChange={e => update('referenceUrl', e.target.value)} />
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="actions">
        <button className="btn btn-primary" onClick={handleNext}>Next: EAT & client info →</button>
      </div>
    </div>
  );
}

// ── Step 2: EAT info ──────────────────────────────────────────
function StepEAT({ clientData, savedClient, onBack, onNext }) {
  const [form, setForm] = useState({
    phone: savedClient?.phone || '',
    email: savedClient?.email || '',
    years_trading: savedClient?.years_trading || '',
    team_size: savedClient?.team_size || '',
    accreditations: savedClient?.accreditations || '',
    awards: savedClient?.awards || '',
    clients_served: savedClient?.clients_served || '',
    notable_clients: savedClient?.notable_clients || '',
    stats: savedClient?.stats || '',
    unique_selling_points: savedClient?.unique_selling_points || '',
    pricing_info: savedClient?.pricing_info || '',
    include_pricing: savedClient?.include_pricing !== false,
  });
  const [saving, setSaving] = useState(false);
  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleNext = async () => {
    // Save back to client if one is selected
    if (savedClient?.id) {
      setSaving(true);
      await supabase.from('clients').update(form).eq('id', savedClient.id);
      setSaving(false);
    }
    onNext(form);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Contact details</div>
        <div className="card-sub">Used in calls to action. Pre-filled if saved against this client.</div>
        <div className="form-row form-row-2 form-group">
          <div><label>Phone number</label><input type="text" placeholder="e.g. 01737 773 768" value={form.phone} onChange={e => update('phone', e.target.value)} /></div>
          <div><label>Email address</label><input type="email" placeholder="e.g. info@business.co.uk" value={form.email} onChange={e => update('email', e.target.value)} /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Expertise, Authority & Trust signals</div>
        <div className="card-sub">All optional — the more detail, the more specific the content.</div>
        <div className="form-row form-row-2 form-group">
          <div><label>Years trading</label><input type="text" placeholder="e.g. 15 years, since 2008" value={form.years_trading} onChange={e => update('years_trading', e.target.value)} /></div>
          <div><label>Team size</label><input type="text" placeholder="e.g. 12 full-time staff" value={form.team_size} onChange={e => update('team_size', e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Accreditations & certifications</label><input type="text" placeholder="e.g. Which? Trusted Trader, NCCA member" value={form.accreditations} onChange={e => update('accreditations', e.target.value)} /></div>
        <div className="form-group"><label>Awards & recognition</label><input type="text" placeholder="e.g. Best Local Business 2023" value={form.awards} onChange={e => update('awards', e.target.value)} /></div>
        <div className="form-row form-row-2 form-group">
          <div><label>Clients / jobs completed</label><input type="text" placeholder="e.g. Over 2,000 customers" value={form.clients_served} onChange={e => update('clients_served', e.target.value)} /></div>
          <div><label>Notable clients</label><input type="text" placeholder="e.g. NHS, Tesco, local councils" value={form.notable_clients} onChange={e => update('notable_clients', e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Key stats</label><textarea rows={2} placeholder="e.g. 98% satisfaction, 500+ Google reviews" value={form.stats} onChange={e => update('stats', e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>Unique selling points</label><textarea rows={2} placeholder="e.g. Same-day service, eco-friendly, 100% guarantee" value={form.unique_selling_points} onChange={e => update('unique_selling_points', e.target.value)} /></div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Pricing</div>
        <div className="card-sub">Including prices can boost conversions for some businesses, but not all.</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0, fontWeight: 400, fontSize: 13 }}>
            <input type="radio" checked={form.include_pricing !== false} onChange={() => update('include_pricing', true)} />
            Include pricing
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0, fontWeight: 400, fontSize: 13 }}>
            <input type="radio" checked={form.include_pricing === false} onChange={() => update('include_pricing', false)} />
            Do not mention pricing
          </label>
        </div>
        {form.include_pricing !== false && (
          <textarea rows={2} placeholder="e.g. Prices start from £25, free quotes available" value={form.pricing_info} onChange={e => update('pricing_info', e.target.value)} />
        )}
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={onBack}>← Back</button>
        <button className="btn btn-primary" onClick={handleNext} disabled={saving}>
          {saving ? <><span className="spinner" /> Saving…</> : 'Generate content brief →'}
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Brief + content ───────────────────────────────────
function StepBrief({ keywordData, eatInfo, onBack }) {
  const [brief, setBrief] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [writing, setWriting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [regenTitle, setRegenTitle] = useState(false);
  const [regenDesc, setRegenDesc] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { generateTheBrief(); }, []);

  const generateTheBrief = async () => {
    setGenerating(true);
    setError('');
    try {
      const result = await generateBrief(
        keywordData.keyword, keywordData.service, keywordData.location,
        null, null, keywordData.referenceUrl,
        keywordData.businessName, keywordData.description, eatInfo
      );
      setBrief(result.brief);
      setMetaTitle(result.brief.metaTitle || '');
      setMetaDesc(result.brief.metaDescription || '');
    } catch (e) {
      setError('Failed to generate brief — please try again.');
    }
    setGenerating(false);
  };

  const handleWrite = async () => {
    setWriting(true);
    try {
      const result = await writeContent(
        { ...brief, eatInfo },
        keywordData.businessName,
        keywordData.description,
        keywordData.referenceUrl
      );
      setContent(result.content);
    } catch (e) { setError('Failed to write content — please try again.'); }
    setWriting(false);
  };

  const handleRegenTitle = async () => {
    setRegenTitle(true);
    try {
      const { regenerateMeta: regen } = await import('../utils/api');
      const result = await regen(keywordData.keyword, keywordData.businessName, metaTitle, metaDesc, 'title');
      if (result.metaTitle) setMetaTitle(result.metaTitle);
    } catch (e) {}
    setRegenTitle(false);
  };

  const handleRegenDesc = async () => {
    setRegenDesc(true);
    try {
      const { regenerateMeta: regen } = await import('../utils/api');
      const result = await regen(keywordData.keyword, keywordData.businessName, metaTitle, metaDesc, 'description');
      if (result.metaDescription) setMetaDesc(result.metaDescription);
    } catch (e) {}
    setRegenDesc(false);
  };

  const handleExport = async () => {
    if (!content) return;
    setExporting(true);
    try {
      await exportContentDoc(content, metaTitle, metaDesc, keywordData.keyword, keywordData.businessName, brief?.imageIdeas || []);
    } catch (e) { setError('Export failed — please try again.'); }
    setExporting(false);
  };

  if (generating) return (
    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
      <span className="spinner" style={{ width: 24, height: 24 }} />
      <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 12 }}>Generating content brief…</div>
    </div>
  );

  if (!brief) return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}
      <button className="btn btn-primary" onClick={generateTheBrief}>Retry</button>
    </div>
  );

  const imageIdeas = brief.imageIdeas || [];

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Meta data */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Meta data</div>
        </div>

        {/* Title tag */}
        <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', padding: '10px 12px', border: `1px solid ${metaTitle.length > 60 ? 'var(--red)' : 'var(--gray-200)'}`, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Title tag</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <CharCounter text={metaTitle} limit={60} />
              <button className="btn btn-sm" onClick={handleRegenTitle} disabled={regenTitle} style={{ fontSize: 11, padding: '3px 10px' }}>
                {regenTitle ? <><span className="spinner" /> …</> : '↻ Regenerate'}
              </button>
            </div>
          </div>
          <textarea value={metaTitle} onChange={e => setMetaTitle(e.target.value)} rows={2} style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, resize: 'none', outline: 'none', color: 'var(--gray-900)', fontFamily: 'inherit' }} />
        </div>

        {/* Meta description */}
        <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', padding: '10px 12px', border: `1px solid ${metaDesc.length > 156 ? 'var(--red)' : 'var(--gray-200)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Meta description</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <CharCounter text={metaDesc} limit={156} />
              <button className="btn btn-sm" onClick={handleRegenDesc} disabled={regenDesc} style={{ fontSize: 11, padding: '3px 10px' }}>
                {regenDesc ? <><span className="spinner" /> …</> : '↻ Regenerate'}
              </button>
            </div>
          </div>
          <textarea value={metaDesc} onChange={e => setMetaDesc(e.target.value)} rows={3} style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, resize: 'none', outline: 'none', color: 'var(--gray-900)', fontFamily: 'inherit' }} />
        </div>
      </div>

      {/* Page structure */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">{brief.suggestedH1}</div>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
          {brief.pageType} · Target {brief.wordCountTarget} words
          {brief.secondaryKeywords?.length > 0 && (
            <> · Secondary: {brief.secondaryKeywords.slice(0, 3).join(', ')}</>
          )}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {brief.sections?.map((s, i) => {
            const colors = STAGE_COLORS[s.stage] || STAGE_COLORS['Additional'];
            return (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: colors.bg, border: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: colors.border, color: colors.color, flexShrink: 0, alignSelf: 'flex-start', marginTop: 1 }}>{s.stage}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.color, marginBottom: 3 }}>{s.element} <span style={{ fontWeight: 400, opacity: 0.7 }}>{s.wordCount} words</span></div>
                  <div style={{ fontSize: 12, color: colors.color, opacity: 0.85, lineHeight: 1.5 }}>{s.instructions}</div>
                </div>
              </div>
            );
          })}
        </div>

        {brief.seoNotes && (
          <div style={{ background: 'var(--blue-light)', border: '1px solid var(--blue-border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SEO notes</div>
            <div style={{ fontSize: 12, color: 'var(--blue)', lineHeight: 1.6 }}>{brief.seoNotes}</div>
          </div>
        )}
      </div>

      {/* Image ideas */}
      {imageIdeas.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-title">Image file names & alt text</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {imageIdeas.map((img, i) => (
              <div key={i} style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', flexShrink: 0 }}>File name:</span>
                  <code style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', padding: '1px 6px', borderRadius: 4 }}>{img.fileName}</code>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--gray-400)' }} onClick={() => navigator.clipboard.writeText(img.fileName)}>Copy</button>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', flexShrink: 0 }}>Alt text:</span>
                  <span style={{ fontSize: 12, color: 'var(--gray-800)', flex: 1 }}>{img.altText}</span>
                  <span style={{ fontSize: 11, color: img.altText.length > 125 ? 'var(--red)' : 'var(--green)', flexShrink: 0 }}>{img.altText.length}/125</span>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--gray-400)' }} onClick={() => navigator.clipboard.writeText(img.altText)}>Copy</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content editor */}
      {content && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Generated content</div>
            <button className="btn btn-sm" onClick={() => navigator.clipboard.writeText(content)}>Copy all</button>
          </div>
          <textarea value={content} onChange={e => setContent(e.target.value)} rows={24} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7 }} />
        </div>
      )}

      {/* Actions */}
      <div className="actions" style={{ borderTop: 'none', paddingTop: 0, flexWrap: 'wrap', gap: 8 }}>
        <button className="btn" onClick={onBack}>← Back</button>
        <button className="btn" onClick={generateTheBrief} disabled={generating}>↻ Regenerate brief</button>
        <button className="btn btn-primary" onClick={handleWrite} disabled={writing}>
          {writing ? <><span className="spinner" /> Writing…</> : content ? 'Regenerate content' : 'Write content with AI'}
        </button>
        {content && (
          <button className="btn btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? <><span className="spinner" /> Exporting…</> : '↓ Download Word doc'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main tool ─────────────────────────────────────────────────
export default function ContentBriefTool({ onBack }) {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [keywordData, setKeywordData] = useState(null);
  const [savedClient, setSavedClient] = useState(null);
  const [eatInfo, setEatInfo] = useState(null);

  useEffect(() => {
    supabase.from('clients').select('id, name, url, description, phone, email, years_trading, team_size, accreditations, awards, clients_served, notable_clients, stats, unique_selling_points, pricing_info, include_pricing')
      .order('name').then(({ data }) => { if (data) setClients(data); });
  }, []);

  const handleStep1 = async (data) => {
    setKeywordData(data);
    // Load saved client EAT info if a client was selected
    if (data.clientId) {
      const client = clients.find(c => c.id === data.clientId);
      setSavedClient(client || null);
    } else {
      setSavedClient(null);
    }
    setStep(2);
  };

  const handleStep2 = (eat) => {
    setEatInfo(eat);
    setStep(3);
  };

  const stepLabels = ['Keyword & client', 'EAT & trust signals', 'Brief & content'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Content Brief & Writer</div>
          <div className="page-sub">Generate an AIDA + E-A-T content brief and write the full page copy</div>
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', alignItems: 'center' }}>
        {stepLabels.map((label, i) => {
          const n = i + 1;
          const isDone = step > n;
          const isActive = step === n;
          return (
            <React.Fragment key={n}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 600,
                  background: isDone ? '#3B6D11' : isActive ? '#185FA5' : 'var(--gray-200)',
                  color: isDone || isActive ? '#fff' : 'var(--gray-500)',
                  cursor: isDone ? 'pointer' : 'default'
                }}
                  onClick={() => isDone && setStep(n)}
                >
                  {isDone ? '✓' : n}
                </div>
                <span style={{ fontSize: 13, color: isActive ? 'var(--gray-900)' : 'var(--gray-500)', fontWeight: isActive ? 500 : 400 }}>{label}</span>
              </div>
              {i < stepLabels.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--gray-200)', margin: '0 12px' }} />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && <StepInput clients={clients} onNext={handleStep1} />}
      {step === 2 && <StepEAT clientData={keywordData} savedClient={savedClient} onBack={() => setStep(1)} onNext={handleStep2} />}
      {step === 3 && <StepBrief keywordData={keywordData} eatInfo={eatInfo} onBack={() => setStep(2)} />}
    </div>
  );
}
